import React, { useEffect, useState, useCallback, useRef } from "react";
import { api, fmtINR } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CalendarBlank, Plus, Trash, FloppyDisk, X, CaretDown, PencilSimple } from "@phosphor-icons/react";

const FIELDS_WITH_OPTIONS = ["task", "vendor", "location", "frequency"];

export default function UpcomingExpensesTab({ refreshKey, onChange }) {
  const [items, setItems] = useState([]);
  const [options, setOptions] = useState({ task: [], vendor: [], location: [], frequency: [] });
  const [loading, setLoading] = useState(true);
  const [newRow, setNewRow] = useState(blankRow());
  // Locked by default — the table is read-only until the user clicks Edit.
  const [locked, setLocked] = useState(true);

  function blankRow() {
    return { task: "", vendor: "", location: "", frequency: "", due_day: "", amount: "", is_active: true };
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rRows, rOpts] = await Promise.all([
        api.get("/payment-tasks"),
        api.get("/payment-tasks/options"),
      ]);
      // Collapse the response into a row per task id (the API expands due_days)
      const seen = new Map();
      (rRows.data?.items || []).forEach((it) => {
        if (!seen.has(it.id)) seen.set(it.id, it);
      });
      setItems(Array.from(seen.values()));
      setOptions(rOpts.data || {});
    } catch {
      toast.error("Failed to load upcoming expenses");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  // ---- mutations ----------------------------------------------------------
  const addRow = async () => {
    if (!newRow.task || !newRow.task.trim()) {
      toast.error("Task is required");
      return;
    }
    try {
      await api.post("/payment-tasks", {
        ...newRow,
        amount: newRow.amount === "" ? null : Number(newRow.amount),
      });
      toast.success("Added");
      setNewRow(blankRow());
      await load();
      onChange && onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add");
    }
  };

  const updateRow = async (id, patch) => {
    try {
      await api.patch(`/payment-tasks/${id}`, patch);
      // Optimistic merge so the UI doesn't flicker the dropdown on toggle
      setItems((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch, due_days: patch.due_day != null ? (patch.due_day ? [Number(patch.due_day)] : []) : r.due_days } : r)));
      onChange && onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
      await load();
    }
  };

  const deleteRow = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try {
      await api.delete(`/payment-tasks/${id}`);
      toast.success("Deleted");
      await load();
      onChange && onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const addOption = async (field, value) => {
    const v = (value || "").trim();
    if (!v) return;
    try {
      await api.post(`/payment-tasks/options/${field}`, { value: v });
      setOptions((o) => ({ ...o, [field]: Array.from(new Set([...(o[field] || []), v])).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())) }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add option");
    }
  };

  const deleteOption = async (field, value) => {
    if (!window.confirm(`Remove "${value}" from the ${field} dropdown? (Existing rows using this value are not affected.)`)) return;
    try {
      await api.delete(`/payment-tasks/options/${field}`, { params: { value } });
      setOptions((o) => ({ ...o, [field]: (o[field] || []).filter((x) => x !== value) }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete option");
    }
  };

  return (
    <div data-testid="upcoming-expenses-tab" className="space-y-5">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-sm grid place-items-center bg-cyan-900/40 text-cyan-200">
              <CalendarBlank size={15} weight="duotone" />
            </div>
            <h2 className="font-heading font-bold text-2xl tracking-tight">Upcoming Expenses</h2>
          </div>
          <div className="text-[11px] text-slate-400 mt-1.5">
            Recurring obligations (rent, salaries, EMIs, GST, etc.). Active rows feed Committed Outflow on Cash Health.
            Use the dropdowns to reuse vendors / locations / frequencies across rows.
          </div>
        </div>

        <div className="flex items-center gap-2" data-testid="upcoming-expenses-toolbar">
          {locked ? (
            <Button
              size="sm"
              onClick={() => setLocked(false)}
              className="h-9 rounded-sm bg-cyan-600 hover:bg-cyan-500 text-white"
              data-testid="upcoming-edit-btn"
            >
              <PencilSimple size={14} weight="bold" className="mr-1.5" />
              Edit
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={() => setLocked(true)}
              className="h-9 rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white"
              data-testid="upcoming-save-btn"
            >
              <FloppyDisk size={14} weight="bold" className="mr-1.5" />
              Save
            </Button>
          )}
          <div className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-sm border" style={{
            background: locked ? "#0f1729" : "#3b2406",
            borderColor: locked ? "#1e3a8a" : "#b45309",
            color: locked ? "#94a3b8" : "#fbbf24",
          }} data-testid="upcoming-lock-badge">
            {locked ? "Read-only" : "Editing"}
          </div>
        </div>
      </div>

      <div className="rounded-md border border-cyan-900/40 bg-slate-900/40 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="upcoming-expenses-table">
            <thead>
              <tr className="bg-slate-900/80 text-[10px] uppercase tracking-wider text-cyan-200 font-bold">
                <th className="text-left px-3 py-3 min-w-[180px]">Task</th>
                <th className="text-left px-3 py-3 min-w-[180px]">Party Name</th>
                <th className="text-left px-3 py-3 min-w-[150px]">Location</th>
                <th className="text-left px-3 py-3 min-w-[120px]">Frequency</th>
                <th className="text-left px-3 py-3 min-w-[100px]">Due Date</th>
                <th className="text-right px-3 py-3 min-w-[110px]">Amount</th>
                <th className="text-left px-3 py-3 min-w-[110px]">Status</th>
                <th className="text-right px-3 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {/* New row entry — only visible in Editing mode */}
              {!locked && (
              <tr className="border-t border-cyan-900/40 bg-cyan-900/10" data-testid="upcoming-expenses-new-row">
                <td className="px-3 py-2">
                  <ComboBox testid="new-task" value={newRow.task} options={options.task} onChange={(v) => setNewRow({ ...newRow, task: v })}
                    onAddOption={(v) => addOption("task", v)} onDeleteOption={(v) => deleteOption("task", v)} placeholder="e.g. Rent" />
                </td>
                <td className="px-3 py-2">
                  <ComboBox testid="new-vendor" value={newRow.vendor} options={options.vendor} onChange={(v) => setNewRow({ ...newRow, vendor: v })}
                    onAddOption={(v) => addOption("vendor", v)} onDeleteOption={(v) => deleteOption("vendor", v)} placeholder="Party name" />
                </td>
                <td className="px-3 py-2">
                  <ComboBox testid="new-location" value={newRow.location} options={options.location} onChange={(v) => setNewRow({ ...newRow, location: v })}
                    onAddOption={(v) => addOption("location", v)} onDeleteOption={(v) => deleteOption("location", v)} placeholder="Location" />
                </td>
                <td className="px-3 py-2">
                  <ComboBox testid="new-frequency" value={newRow.frequency} options={options.frequency} onChange={(v) => setNewRow({ ...newRow, frequency: v })}
                    onAddOption={(v) => addOption("frequency", v)} onDeleteOption={(v) => deleteOption("frequency", v)} placeholder="Monthly / Weekly" />
                </td>
                <td className="px-3 py-2">
                  <DayDropdown value={newRow.due_day} onChange={(v) => setNewRow({ ...newRow, due_day: v })} testid="new-due-day" />
                </td>
                <td className="px-3 py-2">
                  <input type="number" min="0" step="1" value={newRow.amount} onChange={(e) => setNewRow({ ...newRow, amount: e.target.value })}
                    placeholder="0" className="w-full h-8 px-2 text-xs font-mono tabular-nums text-right rounded-sm bg-slate-950 border border-cyan-900/50 text-cyan-100 focus:outline-none focus:ring-1 focus:ring-cyan-400"
                    data-testid="new-amount" />
                </td>
                <td className="px-3 py-2">
                  <ActiveDropdown value={newRow.is_active} onChange={(v) => setNewRow({ ...newRow, is_active: v })} testid="new-active" />
                </td>
                <td className="px-2 py-2 text-right">
                  <button onClick={addRow} className="h-8 px-2.5 text-xs font-bold rounded-sm bg-cyan-600 hover:bg-cyan-500 text-white inline-flex items-center gap-1" data-testid="new-add-btn">
                    <Plus size={12} weight="bold" /> Add
                  </button>
                </td>
              </tr>
              )}

              {/* Existing rows */}
              {loading && <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-400 text-xs">Loading…</td></tr>}
              {!loading && items.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-10 text-center text-slate-500 text-xs">No upcoming expenses yet. Click <span className="font-bold text-cyan-300">Edit</span> above to add rows.</td></tr>
              )}
              {!loading && items.map((row) => (
                <ExpenseRow key={row.id} row={row} options={options}
                  locked={locked}
                  onUpdate={(patch) => updateRow(row.id, patch)}
                  onDelete={() => deleteRow(row.id)}
                  onAddOption={addOption}
                  onDeleteOption={deleteOption}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ExpenseRow({ row, options, onUpdate, onDelete, onAddOption, onDeleteOption, locked }) {
  const due = (row.due_days && row.due_days[0]) || "";
  return (
    <tr className="border-t border-cyan-900/30 hover:bg-slate-900/30" data-testid={`expense-row-${row.id}`}>
      <td className="px-3 py-2">
        {locked ? <ReadOnly value={row.task} testid={`row-task-${row.id}`} />
          : <ComboBox value={row.task || ""} options={options.task} onChange={(v) => onUpdate({ task: v })}
              onAddOption={(v) => onAddOption("task", v)} onDeleteOption={(v) => onDeleteOption("task", v)} testid={`row-task-${row.id}`} />}
      </td>
      <td className="px-3 py-2">
        {locked ? <ReadOnly value={row.vendor} testid={`row-vendor-${row.id}`} />
          : <ComboBox value={row.vendor || ""} options={options.vendor} onChange={(v) => onUpdate({ vendor: v })}
              onAddOption={(v) => onAddOption("vendor", v)} onDeleteOption={(v) => onDeleteOption("vendor", v)} testid={`row-vendor-${row.id}`} />}
      </td>
      <td className="px-3 py-2">
        {locked ? <ReadOnly value={row.location} testid={`row-location-${row.id}`} />
          : <ComboBox value={row.location || ""} options={options.location} onChange={(v) => onUpdate({ location: v })}
              onAddOption={(v) => onAddOption("location", v)} onDeleteOption={(v) => onDeleteOption("location", v)} testid={`row-location-${row.id}`} />}
      </td>
      <td className="px-3 py-2">
        {locked ? <ReadOnly value={row.frequency} testid={`row-frequency-${row.id}`} />
          : <ComboBox value={row.frequency || ""} options={options.frequency} onChange={(v) => onUpdate({ frequency: v })}
              onAddOption={(v) => onAddOption("frequency", v)} onDeleteOption={(v) => onDeleteOption("frequency", v)} testid={`row-frequency-${row.id}`} />}
      </td>
      <td className="px-3 py-2">
        {locked ? <ReadOnly value={due ? ordinal(Number(due)) : ""} testid={`row-due-${row.id}`} />
          : <DayDropdown value={due} onChange={(v) => onUpdate({ due_day: v })} testid={`row-due-${row.id}`} />}
      </td>
      <td className="px-3 py-2">
        {locked
          ? <div className="w-full h-8 px-2 text-xs font-mono tabular-nums text-right text-cyan-100 leading-8 truncate" data-testid={`row-amount-${row.id}`}>{row.amount != null ? row.amount.toLocaleString("en-IN") : "—"}</div>
          : <input type="number" min="0" step="1" defaultValue={row.amount ?? ""} onBlur={(e) => {
              const v = e.target.value === "" ? null : Number(e.target.value);
              if (v !== row.amount) onUpdate({ amount: v });
            }}
            className="w-full h-8 px-2 text-xs font-mono tabular-nums text-right rounded-sm bg-slate-950 border border-cyan-900/50 text-cyan-100 focus:outline-none focus:ring-1 focus:ring-cyan-400"
            data-testid={`row-amount-${row.id}`} />}
      </td>
      <td className="px-3 py-2">
        {locked
          ? <div className={`w-full h-8 px-2 text-xs font-semibold leading-8 rounded-sm border ${row.is_active !== false ? "bg-emerald-950/40 border-emerald-700/40 text-emerald-200" : "bg-slate-950 border-slate-700 text-slate-400"}`} data-testid={`row-active-${row.id}`}>
              {row.is_active !== false ? "Active" : "Inactive"}
            </div>
          : <ActiveDropdown value={row.is_active !== false} onChange={(v) => onUpdate({ is_active: v })} testid={`row-active-${row.id}`} />}
      </td>
      <td className="px-2 py-2 text-right">
        {!locked && (
          <button onClick={onDelete} className="text-slate-500 hover:text-rose-400 p-1 rounded-sm" title="Delete this row" data-testid={`row-delete-${row.id}`}>
            <Trash size={14} weight="bold" />
          </button>
        )}
      </td>
    </tr>
  );
}

function ReadOnly({ value, testid }) {
  return (
    <div className="w-full h-8 px-2 text-xs text-cyan-100 leading-8 truncate" data-testid={testid}>
      {value || <span className="text-slate-600">—</span>}
    </div>
  );
}

function DayDropdown({ value, onChange, testid }) {
  return (
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full h-8 px-2 text-xs rounded-sm bg-slate-950 border border-cyan-900/50 text-cyan-100 focus:outline-none focus:ring-1 focus:ring-cyan-400">
      <option value="">—</option>
      {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
        <option key={d} value={d}>{ordinal(d)}</option>
      ))}
    </select>
  );
}

function ActiveDropdown({ value, onChange, testid }) {
  return (
    <select value={value ? "active" : "inactive"} onChange={(e) => onChange(e.target.value === "active")} data-testid={testid}
      className={`w-full h-8 px-2 text-xs rounded-sm border focus:outline-none focus:ring-1 focus:ring-cyan-400 font-semibold ${
        value ? "bg-emerald-950/50 border-emerald-700/50 text-emerald-200" : "bg-slate-950 border-slate-700 text-slate-400"
      }`}>
      <option value="active">Active</option>
      <option value="inactive">Inactive</option>
    </select>
  );
}

function ComboBox({ value, options = [], onChange, onAddOption, onDeleteOption, placeholder, testid }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const wrapRef = useRef(null);

  useEffect(() => { setDraft(value || ""); }, [value]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const filtered = options.filter((o) => o.toLowerCase().includes((draft || "").toLowerCase()));
  const canAdd = draft && draft.trim() && !options.some((o) => o.toLowerCase() === draft.trim().toLowerCase());

  const commit = (val) => {
    setDraft(val);
    onChange(val);
    setOpen(false);
  };

  const handleAdd = async () => {
    const v = draft.trim();
    if (!v) return;
    await onAddOption(v);
    commit(v);
  };

  return (
    <div ref={wrapRef} className="relative" data-testid={testid}>
      <div className="relative">
        <input
          type="text"
          value={draft}
          onChange={(e) => { setDraft(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => { if (draft !== value) onChange(draft); }}
          placeholder={placeholder}
          className="w-full h-8 pl-2 pr-7 text-xs rounded-sm bg-slate-950 border border-cyan-900/50 text-cyan-100 placeholder:text-slate-600 focus:outline-none focus:ring-1 focus:ring-cyan-400"
        />
        <button type="button" onClick={() => setOpen((o) => !o)} className="absolute right-1 top-1/2 -translate-y-1/2 text-slate-500 hover:text-cyan-300 p-0.5">
          <CaretDown size={11} weight="bold" />
        </button>
      </div>
      {open && (
        <div className="absolute z-50 mt-1 left-0 right-0 max-h-60 overflow-auto rounded-sm border border-cyan-700/60 bg-slate-900 shadow-lg" data-testid={`${testid}-dropdown`}>
          {filtered.length === 0 && !canAdd && (
            <div className="px-2 py-2 text-[11px] text-slate-500">No options</div>
          )}
          {filtered.map((opt) => (
            <div key={opt} className="flex items-center group/opt hover:bg-slate-800">
              <button type="button" onMouseDown={(e) => { e.preventDefault(); commit(opt); }}
                className="flex-1 text-left px-2 py-1.5 text-xs text-cyan-100" data-testid={`${testid}-opt-${opt.replace(/\s+/g, '_')}`}>
                {opt}
              </button>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); onDeleteOption(opt); }}
                className="opacity-0 group-hover/opt:opacity-100 px-1.5 py-1 text-slate-500 hover:text-rose-400 transition-opacity"
                title={`Remove "${opt}" from dropdown`}>
                <Trash size={11} weight="bold" />
              </button>
            </div>
          ))}
          {canAdd && (
            <button type="button" onMouseDown={(e) => { e.preventDefault(); handleAdd(); }}
              className="w-full flex items-center gap-1.5 text-left px-2 py-1.5 text-xs font-semibold text-cyan-300 hover:bg-cyan-950/60 border-t border-cyan-900/40"
              data-testid={`${testid}-add-new`}>
              <Plus size={11} weight="bold" /> Add "{draft.trim()}"
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function ordinal(n) {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}
